import React, { useState } from 'react';
import { X, MapPin, Home as HomeIcon, Search } from 'lucide-react';

const EditAddressModal = ({ 
  isOpen, 
  onClose, 
  userProfile, 
  setUserProfile,
  searchAddress,
  addressSuggestions,
  showAddressSuggestions,
  selectAddress 
}) => {

  const [localAddress, setLocalAddress] = useState(userProfile.address || '');
  const [localPincode, setLocalPincode] = useState(userProfile.pincode || '');


  if (!isOpen) return null;

   const handleAddressChange = (value) => {
    setLocalAddress(value);
    searchAddress(value);
  };

  const handleSelectSuggestion = (suggestion) => {
    setLocalAddress(suggestion.address);
    setLocalPincode(suggestion.pincode);
    selectAddress(suggestion);
  };

  const handleSaveAddress = () => {
    if (!localAddress.trim()) {
      alert('Please enter your address');
      return;
    }

    if (!localPincode.trim() || localPincode.length !== 6) {
      alert('Please enter a valid 6-digit pincode');
      return;
    }

    setUserProfile({
      ...userProfile,
      address: localAddress,
      pincode: localPincode
    });

    alert('✅ Address updated successfully!');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end z-50">
      <div className="bg-white w-full max-w-md mx-auto rounded-t-3xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-800">Edit Address</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:bg-gray-100 rounded-full p-2 transition-all"
          >
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Address Input with Search */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Delivery Address *
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                value={localAddress}
                onChange={(e) => handleAddressChange(e.target.value)}
                placeholder="Search for your address..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
            </div>

            {/* Address Suggestions Dropdown */}
            {showAddressSuggestions && addressSuggestions.length > 0 && (
              <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                {addressSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSelectSuggestion(suggestion)}
                    className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-all flex items-start gap-3 border-b border-gray-100 last:border-b-0"
                  >
                    <MapPin size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-gray-800 font-medium">
                        {suggestion.address}
                      </p>
                      <p className="text-xs text-gray-500">
                        Pincode: {suggestion.pincode}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Pincode Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Pincode *
            </label>
            <input
              type="text"
              value={localPincode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                if (value.length <= 6) {
                  setLocalPincode(value);
                }
              }}
              placeholder="Enter 6-digit pincode"
              maxLength={6}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>

          {/* Current Address Display */}
          {userProfile.address && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <HomeIcon size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-semibold text-gray-800 text-sm mb-1">
                    Current Address
                  </h4>
                  <p className="text-xs text-gray-600">
                    {userProfile.address}
                  </p>
                  {userProfile.pincode && (
                    <p className="text-xs text-gray-500 mt-1">
                      Pincode: {userProfile.pincode}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              onClick={onClose}
              className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-xl font-medium hover:bg-gray-300 transition-all"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveAddress}
              className="flex-1 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium hover:shadow-lg transition-all"
            >
              Save Address
            </button>
          </div>
        </div>

        {/* Info Section */}
        <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-4">
          <h4 className="font-semibold text-gray-800 text-sm mb-2">
            📍 Delivery Information
          </h4>
          <p className="text-xs text-gray-600">
            Make sure your address is correct for timely delivery. You can update it anytime.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EditAddressModal;